<script setup lang="ts">
import { Heading } from '~ds/components/ui/typography';
import KUIUnifiedSpotlight from '~kui/components/KUI/Unified/Spotlight/KUIUnifiedSpotlight.vue';
import { useMotionPresets } from '~kui/composables/useMotionPresets';
import { motion } from 'motion-v';

import type { CompletionSpotlight, CompletionSpotlightLinkClick } from './types';

/**
 * The completion step for spotlights with no badge to ride on: the heading and the spotlight
 * cards, nothing else. A completion with a badge shows its spotlights inside the badge step
 * instead ({@see KUIUniCompletionProgress}).
 */
interface Props {
  heading: string;
  spotlights: CompletionSpotlight[];
}

const props = defineProps<Props>();

const emit = defineEmits<{
  spotlightLinkClick: [click: CompletionSpotlightLinkClick];
}>();

const { SLIDE_UP, TRANSITION } = useMotionPresets();

const shownSpotlights = computed(() =>
  props.spotlights.filter(spotlight => spotlight.links.length > 0),
);
</script>

<template>
  <div class="flex w-full flex-col items-center gap-6 sm:gap-10">
    <motion.div
      class="text-center"
      v-bind="SLIDE_UP"
      :transition="TRANSITION"
    >
      <Heading
        size="md"
        as="h2"
        class="max-w-xs leading-tight sm:text-2xl"
      >
        {{ heading }}
      </Heading>
    </motion.div>

    <!-- z-40 clears the completion screen's tap overlay (z-30) so a link tap reaches the link
         and never the step machine. The card sits over the busy completion background, so it
         carries a shadow the white placements don't need. -->
    <motion.div
      class="relative z-40 flex w-full max-w-sm flex-col gap-3"
      v-bind="SLIDE_UP"
      :transition="{ ...TRANSITION, delay: 0.1 }"
    >
      <KUIUnifiedSpotlight
        v-for="spotlight in shownSpotlights"
        :key="spotlight.key"
        :heading="spotlight.heading"
        :image-url="spotlight.imageUrl"
        :image-alt="spotlight.imageAlt"
        :links="spotlight.links"
        class="w-full text-start shadow-lg"
        data-testid="completion-spotlight"
        @link-click="emit('spotlightLinkClick', { key: spotlight.key, link: $event })"
      />
    </motion.div>
  </div>
</template>
